import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Box, TextField, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { createESMIFJob } from '@/api/jobs';
import { ESMIFForm } from '@/components/ModelForms';
import { useAwsSettings } from '@/hooks/useAwsSettings';
import { ESMIFParams } from '@/types/colabfold';
import JobFormHeader from '@/components/JobFormHeader';
import { ModelType } from '@/types/colabfold';

const ESMIFTool: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = React.useState<ESMIFParams>({
    jobname: `ESM-IF-${new Date().toISOString().split('T')[0]}`,
    num_designs: 1,
    temperature: 0.5,
    input_pdb: '',
    folder: location.state?.folderId || null
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { isComplete: hasCompleteAwsSettings, loading: awsSettingsLoading } = useAwsSettings();

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await createESMIFJob(formData);
      navigate('/jobs'); // Navigate back to the all jobs list after submission
    } catch (error) {
      console.error('Error creating ESM-IF job:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/jobs'); // Navigate back to the all jobs list
  };

  return (
    <Container>
      <Box p={3}>
        <JobFormHeader modelType={ModelType.ESMIF} />
        <Box mb={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {!hasCompleteAwsSettings && !awsSettingsLoading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your AWS settings are incomplete. Please complete your AWS settings before submitting a job.
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Job Name"
            value={formData.jobname}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({...formData, jobname: e.target.value})}
            required
            sx={{ mb: 3 }}
          />
          <ESMIFForm 
            params={formData} 
            onChange={setFormData} 
          />
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button 
              variant="outlined" 
              onClick={handleCancel} 
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmit}
              disabled={!formData.jobname || !formData.input_pdb || !hasCompleteAwsSettings || submitting || awsSettingsLoading}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
`;

export default ESMIFTool; 